/**
 * CardView for Poker Reborn
 *
 * Displays a single playing card using PNG sprite atlas textures.
 * Card faces come from the resolution-aware cards atlas (1x/2x).
 * Card backs come from a separate back image (1x/2x).
 *
 * @see docs/screens/room/CARD_VIEW.md - CardView component reference (sizes, textures, flip, highlight)
 * @see docs/screens/room/CARD_BACKS.md - Card back design
 * @see tasks/done/CARDS_PNG_TRANSITION.md - Migration from procedural to sprite rendering
 */

import { BlurFilter, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { AssetService } from '@/services/AssetService';
import { log } from '@/utils/log';
import type { Card } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

export const CARD_STYLES = {
  /** Card dimensions - small for opponent hole cards */
  small: {
    width: 50,
    height: 70,
    cornerRadius: 5,
  },

  /** Card dimensions - medium for hero hole cards */
  medium: {
    width: 70,
    height: 98,
    cornerRadius: 6,
  },

  /** Card dimensions - large for community cards */
  large: {
    width: 90,
    height: 126,
    cornerRadius: 8,
  },

  /** Colors used by overlays and highlights (card visuals are now sprites) */
  colors: {
    highlight: 0xffd700, // Gold - for core cards (strong highlight)
    highlightSubtle: 0xd3d3d3, // Light gray - for kicker cards (subtle highlight)
  },
};

export type CardSize = 'small' | 'medium' | 'large';

// =============================================================================
// CARD VIEW
// =============================================================================

export class CardView extends Container {
  private cardSize: CardSize;
  private card: Card | null = null;
  private isFaceUp = false; // Start face-down (matches showBack() in constructor)
  private isHighlighted = false;
  private isDarkened = false;

  private frontContainer: Container;
  private backContainer: Container;
  private cardFaceSprite: Sprite;
  private cardBackSprite: Sprite;
  private highlightBorder: Graphics;
  private edgeBorder: Graphics;

  // Effect injection points
  private shadowSprite: Graphics;
  private highlightGlow: Graphics;
  private flipContainer: Container;
  private darkOverlay: Graphics;

  // Animation tracking - prevents race conditions when multiple flips are requested
  private currentAnimationId = 0;

  constructor(size: CardSize = 'medium') {
    super();
    this.cardSize = size;

    const dims = CARD_STYLES[size];

    // Create shadow (behind everything)
    this.shadowSprite = new Graphics();
    this.shadowSprite.label = 'cardShadow';
    this.shadowSprite.roundRect(
      -dims.width / 2 - 4 + 2,
      -dims.height / 2 + 4 + 2,
      dims.width - 4,
      dims.height - 4,
      dims.cornerRadius
    );
    this.shadowSprite.fill({ color: 0x000000, alpha: 0.45 });
    this.shadowSprite.filters = [new BlurFilter({ strength: 3 })];
    this.addChild(this.shadowSprite);

    // Create highlight glow (behind card, for effects)
    this.highlightGlow = new Graphics();
    this.highlightGlow.label = 'highlightGlow';
    this.highlightGlow.visible = false;
    this.addChild(this.highlightGlow);

    // Create flip container (wraps face/back for 3D flip transform)
    this.flipContainer = new Container();
    this.flipContainer.label = 'flipContainer';
    this.addChild(this.flipContainer);

    // Create front container with face sprite - inside flip container
    this.frontContainer = new Container();
    this.flipContainer.addChild(this.frontContainer);

    this.cardFaceSprite = new Sprite(Texture.EMPTY);
    this.cardFaceSprite.anchor.set(0.5);
    this.cardFaceSprite.width = dims.width;
    this.cardFaceSprite.height = dims.height;
    this.frontContainer.addChild(this.cardFaceSprite);

    // Create back container with back sprite - inside flip container
    this.backContainer = new Container();
    this.flipContainer.addChild(this.backContainer);

    this.cardBackSprite = new Sprite(Texture.EMPTY);
    this.cardBackSprite.anchor.set(0.5);
    this.cardBackSprite.width = dims.width;
    this.cardBackSprite.height = dims.height;
    this.backContainer.addChild(this.cardBackSprite);

    // Create dark overlay (for darken effect - above card, below highlight)
    this.darkOverlay = new Graphics();
    this.darkOverlay.label = 'darkOverlay';
    this.darkOverlay.visible = false;
    this.darkOverlay.roundRect(-dims.width / 2, -dims.height / 2, dims.width, dims.height, dims.cornerRadius);
    this.darkOverlay.fill({ color: 0x000000, alpha: 0.5 });
    this.addChild(this.darkOverlay);

    // Create edge border (always visible, covers rough PNG edges when scaled/rotated)
    this.edgeBorder = new Graphics();
    this.edgeBorder.label = 'edgeBorder';
    this.edgeBorder.roundRect(-dims.width / 2, -dims.height / 2, dims.width, dims.height, dims.cornerRadius);
    this.edgeBorder.setStrokeStyle({ width: 1.5, color: 0xffffff });
    this.edgeBorder.stroke();
    this.addChild(this.edgeBorder);

    // Create highlight border (on top of everything)
    this.highlightBorder = new Graphics();
    this.highlightBorder.visible = false;
    this.addChild(this.highlightBorder);

    // Draw initial state (face-down)
    this.drawBack();
    this.showBack();
  }

  /**
   * Set the card to display.
   * Only draws the front if currently face-up.
   * If face-down, the front will be drawn when flip(true) is called.
   */
  setCard(card: Card | null): void {
    this.card = card;
    if (card) {
      log.cards.debug`setCard: ${card.rank}${card.suit}, isFaceUp: ${this.isFaceUp}`;
      // Only draw front if currently face-up to avoid showing face on a back card.
      // showFront() will draw when flipped.
      if (this.isFaceUp) {
        this.drawFront(card);
      }
    } else {
      // Clear sprite texture when card is null to prevent stale face data
      this.cardFaceSprite.texture = Texture.EMPTY;
    }
  }

  /**
   * Get the current card
   */
  getCard(): Card | null {
    return this.card;
  }

  /**
   * Flip the card face up or face down
   */
  flip(faceUp: boolean, animated = true): Promise<void> {
    log.cards.debug`flip(${faceUp}, ${animated}) - current isFaceUp: ${this.isFaceUp}`;

    // Cancel any in-flight animation by incrementing animation ID
    // This ensures we don't have stale animations corrupting state
    if (!animated) {
      this.currentAnimationId++;
    }

    if (this.isFaceUp === faceUp) {
      log.cards.debug`flip skipped - already ${faceUp ? 'face up' : 'face down'}`;
      // Even if state matches, ensure visual state is correct (recovery from corruption)
      if (!animated) {
        if (faceUp) {
          this.showFront();
        } else {
          this.showBack();
        }
      }
      return Promise.resolve();
    }

    this.isFaceUp = faceUp;

    if (animated) {
      return this.animateFlip(faceUp);
    } else {
      if (faceUp) {
        this.showFront();
      } else {
        this.showBack();
      }
      return Promise.resolve();
    }
  }

  /**
   * Highlight the card (e.g., winning hand)
   * @param on - Whether to turn highlight on or off
   * @param level - 'strong' for gold (core cards), 'subtle' for gray (kicker cards)
   */
  highlight(on: boolean, level: 'strong' | 'subtle' = 'strong'): void {
    this.isHighlighted = on;
    this.highlightBorder.visible = on;

    if (on) {
      // Draw the highlight border
      const dims = CARD_STYLES[this.cardSize];
      const { width, height, cornerRadius } = dims;
      const isStrong = level === 'strong';

      // Strong: gold, thicker stroke, more lift
      // Subtle: gray, thinner stroke, less lift
      const highlightColor = isStrong
        ? CARD_STYLES.colors.highlight
        : CARD_STYLES.colors.highlightSubtle;
      const strokeWidth = isStrong ? 4 : 2;
      const liftAmount = isStrong ? 10 : 3;

      this.highlightBorder.clear();
      this.highlightBorder.roundRect(
        -width / 2 - 3,
        -height / 2 - 3,
        width + 6,
        height + 6,
        cornerRadius + 2
      );
      this.highlightBorder.setStrokeStyle({ width: strokeWidth, color: highlightColor });
      this.highlightBorder.stroke();

      // Move card up slightly (less for subtle)
      this.y -= liftAmount;
    } else {
      // Clear the border
      this.highlightBorder.clear();
      // Reset position would need external tracking
    }
  }

  /**
   * Darken the card (e.g., losing hand, folded cards)
   * Uses a dark overlay instead of transparency so table doesn't show through.
   */
  darken(on: boolean): void {
    this.isDarkened = on;
    this.darkOverlay.visible = on;
    this.edgeBorder.tint = on ? 0x808080 : 0xffffff;
  }

  /**
   * Nuclear reset - restore card to pristine initial state.
   * Called at hand start to ensure clean slate.
   */
  resetAll(): void {
    // Cancel any pending animations
    this.currentAnimationId++;

    // Clear card data
    this.card = null;

    // Clear sprite textures
    this.cardFaceSprite.texture = Texture.EMPTY;

    // Reset state flags
    this.isFaceUp = false;
    this.isHighlighted = false;
    this.isDarkened = false;

    // Reset transform
    this.alpha = 1;
    this.scale.set(1);
    this.rotation = 0;

    // Clear highlights and overlays
    this.highlightBorder.clear();
    this.highlightBorder.visible = false;
    this.highlightGlow.clear();
    this.highlightGlow.visible = false;
    this.darkOverlay.visible = false;
    this.edgeBorder.tint = 0xffffff;

    // Show back face
    this.showBack();
  }

  /**
   * Force complete redraw of card graphics.
   * Call this after tab visibility change to restore GPU state.
   * Sprites reference shared atlas textures, so they survive context loss
   * better than procedural Graphics — just re-apply the textures.
   *
   * @see docs/systems/BACKGROUND_TAB_RECOVERY.md
   */
  redraw(): void {
    this.drawBack();
    if (this.isFaceUp && this.card) {
      this.drawFront(this.card);
    }
  }

  /**
   * Get card dimensions
   */
  getCardDimensions(): { width: number; height: number } {
    const dims = CARD_STYLES[this.cardSize];
    return { width: dims.width, height: dims.height };
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawFront(card: Card): void {
    const dims = CARD_STYLES[this.cardSize];
    const texture = AssetService.getInstance().getCardTexture(card.rank, card.suit);
    this.cardFaceSprite.texture = texture;
    this.cardFaceSprite.width = dims.width;
    this.cardFaceSprite.height = dims.height;
  }

  private drawBack(): void {
    const dims = CARD_STYLES[this.cardSize];
    const backTexture = AssetService.getInstance().getCardBackTexture();
    this.cardBackSprite.texture = backTexture;
    this.cardBackSprite.width = dims.width;
    this.cardBackSprite.height = dims.height;
  }

  private showFront(): void {
    log.cards.debug`showFront called, card: ${this.card?.rank}${this.card?.suit}`;
    this.frontContainer.visible = true;
    this.frontContainer.alpha = 1;
    this.backContainer.visible = false;
    this.backContainer.alpha = 0;

    if (this.card) {
      this.drawFront(this.card);
    }
  }

  private showBack(): void {
    // Force front hidden
    this.frontContainer.visible = false;
    this.frontContainer.alpha = 0;

    // Force back visible
    this.backContainer.visible = true;
    this.backContainer.alpha = 1;

    this.drawBack();
  }

  private animateFlip(faceUp: boolean): Promise<void> {
    // Increment animation ID to cancel any in-flight animations
    // This prevents race conditions when flip() is called rapidly
    const animationId = ++this.currentAnimationId;

    return new Promise((resolve) => {
      const duration = 250;
      const startTime = Date.now();
      // Preserve the current scale (set by parent layout's BBS)
      const targetScaleX = this.scale.y; // Use Y scale as reference since X will be animated
      let contentSwapped = false;

      const animate = (): void => {
        // Abort if a newer animation has started
        if (animationId !== this.currentAnimationId) {
          resolve();
          return;
        }

        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        if (progress < 0.5) {
          // First half: scale X down to 0
          this.scale.x = targetScaleX * (1 - progress * 2);
        } else {
          // Second half: scale X up from 0
          // Switch content at midpoint (only once)
          if (!contentSwapped) {
            contentSwapped = true;
            if (faceUp) {
              this.showFront();
            } else {
              this.showBack();
            }
          }
          this.scale.x = targetScaleX * (progress - 0.5) * 2;
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.scale.x = targetScaleX;
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  // ===========================================================================
  // PUBLIC EFFECT INJECTION POINT ACCESSORS
  // ===========================================================================

  /** Get shadow graphics (for shadow effects) */
  getShadow(): Graphics {
    return this.shadowSprite;
  }

  /** Get highlight glow graphics (for CardHighlightEffect) */
  getHighlightGlow(): Graphics {
    return this.highlightGlow;
  }

  /** Get flip container (for CardFlipEffect, CardRevealEffect) */
  getFlipContainer(): Container {
    return this.flipContainer;
  }

  /** Get front container (for face-up card content) */
  getFrontContainer(): Container {
    return this.frontContainer;
  }

  /** Get back container (for face-down card content) */
  getBackContainer(): Container {
    return this.backContainer;
  }

  /** Check if card is face up */
  isFaceUpState(): boolean {
    return this.isFaceUp;
  }

  /**
   * Set highlight glow effect
   * @param color - Glow color
   * @param intensity - Glow intensity (0-1)
   */
  setHighlightGlow(color: number, intensity: number): void {
    const dims = CARD_STYLES[this.cardSize];
    const glowSize = 8;

    this.highlightGlow.clear();

    if (intensity > 0) {
      this.highlightGlow.roundRect(
        -dims.width / 2 - glowSize,
        -dims.height / 2 - glowSize,
        dims.width + glowSize * 2,
        dims.height + glowSize * 2,
        dims.cornerRadius + glowSize / 2
      );
      this.highlightGlow.fill({ color, alpha: intensity * 0.6 });
      this.highlightGlow.visible = true;
    } else {
      this.highlightGlow.visible = false;
    }
  }
}
